'use client';

import { useEffect } from 'react';

// Same Calendly as the outreach emails (dxr_ai_backend email_service.CAL_URL) — one booking link everywhere.
const DEMO_URL = 'https://calendly.com/herukhs-natoe/30min';

/**
 * "Book a demo" — opens Calendly in a self-managed in-page modal.
 *
 * Why this is fast: Calendly's own popup builds a brand-new iframe on click and
 * then fetches the scheduling page + availability (~4s). Instead, we PRELOAD the
 * real scheduling iframe into a hidden modal as soon as the page goes idle or the
 * user first interacts. Opening the modal then just reveals an iframe that's
 * already fully loaded, so it appears instantly. One shared modal/iframe is used
 * regardless of how many "Book a demo" buttons are on the page.
 *
 * The modal stays mounted but transparent + non-interactive while closed, which
 * guarantees the iframe actually renders and loads (more reliable than a
 * display:none preload). It is never torn down, so re-opening is instant too.
 */

let overlayEl = null;
let frameEl = null;
let preloaded = false;
let escHandler = null;

function embedUrl() {
  const host = typeof window !== 'undefined' ? window.location.host : '';
  const p = new URLSearchParams({
    embed_domain: host,
    embed_type: 'Inline',
    hide_gdpr_banner: '1',
  });
  return `${DEMO_URL}?${p.toString()}`;
}

function injectStylesOnce() {
  if (document.getElementById('cal-modal-styles')) return;
  const s = document.createElement('style');
  s.id = 'cal-modal-styles';
  s.textContent = `
    .cal-overlay { position: fixed; inset: 0; z-index: 99999; display: flex; align-items: center; justify-content: center; padding: 20px; background: rgba(5,7,13,0.62); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); opacity: 0; pointer-events: none; transition: opacity 200ms ease; }
    .cal-overlay.is-open { opacity: 1; pointer-events: auto; }
    .cal-modal { position: relative; width: min(1000px, 100%); height: min(720px, 92vh); background: #fff; border-radius: 14px; overflow: hidden; box-shadow: 0 24px 70px rgba(0,0,0,0.45); transform: translateY(10px) scale(0.99); transition: transform 220ms ease; }
    .cal-overlay.is-open .cal-modal { transform: none; }
    .cal-frame { width: 100%; height: 100%; border: 0; display: block; }
    .cal-close { position: absolute; top: 10px; right: 12px; z-index: 2; width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; border: 0; border-radius: 50%; background: rgba(17,17,17,0.06); color: #111; font-size: 22px; line-height: 1; cursor: pointer; transition: background 150ms ease; }
    .cal-close:hover { background: rgba(17,17,17,0.14); }
  `;
  document.head.appendChild(s);
}

function ensurePreloaded(force) {
  if (preloaded || typeof document === 'undefined') return;
  // NEVER inject the iframe before the page's load event. The iframe pulls in Calendly's entire
  // scheduling app (2-4 s), and once it is in the document, window.onload waits for it — which is
  // what analytics report as "page load time". The old behavior preloaded on the user's FIRST
  // pointermove / scroll / touch, i.e. in every session with an interaction, so every recorded
  // Clarity session inherited Calendly's load time (~7 s field average). Passive preload signals
  // now re-arm to fire shortly AFTER load instead; only a real click (force=true) loads sooner.
  if (!force && document.readyState !== 'complete') {
    window.addEventListener('load', () => setTimeout(() => ensurePreloaded(true), 250), { once: true });
    return;
  }
  preloaded = true;
  try {
    injectStylesOnce();
    overlayEl = document.createElement('div');
    overlayEl.className = 'cal-overlay';
    overlayEl.setAttribute('role', 'dialog');
    overlayEl.setAttribute('aria-modal', 'true');
    overlayEl.setAttribute('aria-label', 'Book a demo');
    overlayEl.addEventListener('click', (e) => { if (e.target === overlayEl) closeModal(); });

    const modal = document.createElement('div');
    modal.className = 'cal-modal';

    const close = document.createElement('button');
    close.className = 'cal-close';
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML = '&times;';
    close.addEventListener('click', closeModal);

    frameEl = document.createElement('iframe');
    frameEl.className = 'cal-frame';
    frameEl.title = 'Book a demo with Natoe';
    frameEl.loading = 'eager';
    frameEl.src = embedUrl(); // loads the full scheduler now, while hidden

    modal.appendChild(close);
    modal.appendChild(frameEl);
    overlayEl.appendChild(modal);
    document.body.appendChild(overlayEl);
  } catch (e) {
    /* ignore */
  }
}

function openModal() {
  ensurePreloaded(true); // user asked for it — load now even if the page is still loading
  if (!overlayEl) { window.open(DEMO_URL, '_blank', 'noopener,noreferrer'); return; }
  overlayEl.classList.add('is-open');
  document.body.style.overflow = 'hidden';
  escHandler = (e) => { if (e.key === 'Escape') closeModal(); };
  document.addEventListener('keydown', escHandler);
}

function closeModal() {
  if (!overlayEl) return;
  overlayEl.classList.remove('is-open');
  document.body.style.overflow = '';
  if (escHandler) { document.removeEventListener('keydown', escHandler); escHandler = null; }
}

// Passive preload signal: must NOT pass the event object through — ensurePreloaded's first
// parameter is `force`, and an Event is truthy, which would defeat the wait-for-onload guard.
const preloadPassive = () => ensurePreloaded();

export default function BookDemoButton({ className, style, children = 'Book a demo' }) {
  // Preload on browser idle AND on the user's first interaction anywhere (whichever comes
  // first) — but ensurePreloaded defers all of these until after window.onload, so the hidden
  // iframe never counts against the page's measured load time. ensurePreloaded() is idempotent,
  // so firing from several signals is safe.
  useEffect(() => {
    let idleId, timerId;
    const ric = window.requestIdleCallback;
    if (ric) idleId = ric(preloadPassive, { timeout: 2500 });
    else timerId = setTimeout(preloadPassive, 1500);

    const opts = { once: true, passive: true, capture: true };
    const events = ['pointermove', 'pointerdown', 'scroll', 'keydown', 'touchstart'];
    events.forEach((ev) => window.addEventListener(ev, preloadPassive, opts));
    return () => {
      if (idleId != null && window.cancelIdleCallback) { try { window.cancelIdleCallback(idleId); } catch (e) {} }
      if (timerId) clearTimeout(timerId);
      events.forEach((ev) => window.removeEventListener(ev, preloadPassive, opts));
    };
  }, []);

  const onClick = (e) => { e.preventDefault(); openModal(); };

  return (
    <a
      href={DEMO_URL}
      onClick={onClick}
      onMouseEnter={preloadPassive}
      onFocus={preloadPassive}
      onTouchStart={preloadPassive}
      className={className}
      style={style}
    >
      {children}
    </a>
  );
}
